import { useCallback, useEffect, useRef, useState } from 'react';

import type { ViewerToolMode } from '../cartographerStore';
import type { BoneAssignmentMode } from './BoneAssignments';
import type { ViewerToolDockMappingSectionProps } from './Viewer';

interface UseCartographerMappingSectionOptions {
  hasRenderableSkeleton: boolean;
  mappingHasAssignments: boolean;
  isBoneAssignmentsActive: boolean;
  boneAssignmentsMode: BoneAssignmentMode;
  viewerToolMode: ViewerToolMode;
  showBoneAssignmentsDetailPanel: () => void;
  hideBoneAssignmentsDetailPanel: () => void;
  onBoneAssignmentsToggle: () => void;
  onBoneAssignmentsModeChange: (mode: BoneAssignmentMode) => void;
  runMappingRecognition: () => void;
  clearMappingRecognition: () => void;
}

export default function useCartographerMappingSection({
  hasRenderableSkeleton,
  mappingHasAssignments,
  isBoneAssignmentsActive,
  boneAssignmentsMode,
  viewerToolMode,
  showBoneAssignmentsDetailPanel,
  hideBoneAssignmentsDetailPanel,
  onBoneAssignmentsToggle,
  onBoneAssignmentsModeChange,
  runMappingRecognition,
  clearMappingRecognition,
}: UseCartographerMappingSectionOptions) {
  const [mappingOptionsOpen, setMappingOptionsOpen] = useState(false);
  const [mappingRunAnimationToken, setMappingRunAnimationToken] = useState(0);
  const [mappingRunning, setMappingRunning] = useState(false);
  const mappingRunRafRef = useRef<number | null>(null);

  const cancelPendingMappingRun = useCallback(() => {
    if (mappingRunRafRef.current === null) return;
    cancelAnimationFrame(mappingRunRafRef.current);
    mappingRunRafRef.current = null;
  }, []);

  const resetMappingSection = useCallback(() => {
    setMappingOptionsOpen(false);
    setMappingRunning(false);
    cancelPendingMappingRun();
  }, [cancelPendingMappingRun]);

  useEffect(() => {
    if (hasRenderableSkeleton) return;
    resetMappingSection();
  }, [hasRenderableSkeleton, resetMappingSection]);

  useEffect(
    () => () => {
      cancelPendingMappingRun();
    },
    [cancelPendingMappingRun],
  );

  useEffect(() => {
    if (viewerToolMode === 'select') return;
    if (!mappingOptionsOpen && !isBoneAssignmentsActive) return;

    setMappingOptionsOpen(false);
    if (isBoneAssignmentsActive) {
      hideBoneAssignmentsDetailPanel();
    }
  }, [
    hideBoneAssignmentsDetailPanel,
    isBoneAssignmentsActive,
    mappingOptionsOpen,
    viewerToolMode,
  ]);

  const handleMappingOptionsToggle = useCallback(() => {
    const nextOpen = !mappingOptionsOpen;
    setMappingOptionsOpen(nextOpen);
    if (!hasRenderableSkeleton) return;
    if (nextOpen) {
      showBoneAssignmentsDetailPanel();
      return;
    }
    hideBoneAssignmentsDetailPanel();
  }, [
    hasRenderableSkeleton,
    hideBoneAssignmentsDetailPanel,
    mappingOptionsOpen,
    showBoneAssignmentsDetailPanel,
  ]);

  const handleRunMapping = useCallback(() => {
    if (!hasRenderableSkeleton || mappingRunning) return;

    cancelPendingMappingRun();
    setMappingRunAnimationToken((token) => token + 1);
    setMappingRunning(true);

    // Run on the next paint frames so the icon reset+sweep can start without
    // adding a fixed time delay to recognition itself.
    mappingRunRafRef.current = requestAnimationFrame(() => {
      mappingRunRafRef.current = requestAnimationFrame(() => {
        mappingRunRafRef.current = null;
        try {
          runMappingRecognition();
        } catch (recognitionError) {
          console.warn('Recognition failed:', recognitionError);
        } finally {
          setMappingRunning(false);
        }
      });
    });
  }, [
    cancelPendingMappingRun,
    hasRenderableSkeleton,
    mappingRunning,
    runMappingRecognition,
  ]);

  const handleClearMapping = useCallback(() => {
    clearMappingRecognition();
  }, [clearMappingRecognition]);

  const mappingSection: ViewerToolDockMappingSectionProps = {
    mappingOptionsOpen,
    mappingRunAnimationToken,
    mappingRunning,
    mappingHasAssignments,
    mappingDisabled: !hasRenderableSkeleton,
    runMappingDisabled: !hasRenderableSkeleton || mappingRunning,
    clearMappingDisabled: !mappingHasAssignments,
    boneAssignmentsOpen: isBoneAssignmentsActive,
    boneAssignmentsDisabled: !hasRenderableSkeleton,
    boneAssignmentsMode,
    onMappingOptionsToggle: handleMappingOptionsToggle,
    onRunMapping: handleRunMapping,
    onClearMapping: handleClearMapping,
    onBoneAssignmentsToggle,
    onBoneAssignmentsModeChange,
  };

  return {
    mappingSection,
    resetMappingSection,
  };
}
